'use client';

// Device-scoped "seen" record for market lifecycle modals
// (spec 112-market-lifecycle-modals, FR-005). Each graduation / resolution
// modal is shown at most once per `wallet:market:stage` — the key is built by
// `seenKey()` in `@/lib/market/lifecycle-modal-eligibility`. Persistence is
// purely client-side via localStorage.

import type { StoreApi, UseBoundStore } from 'zustand';
import { createJSONStorage, persist, type StateStorage } from 'zustand/middleware';

import { createTrackedStore } from '@/lib/dev/zustand-registry';

// FR-023: persistent-storage failure must NOT surface to the user. The default
// `localStorage` adapter throws on quota / private-mode failures; wrap every
// call in try/catch so the in-memory state still works when persistence isn't
// available (the modal may then re-show — acceptable per spec).
const SAFE_STORAGE: StateStorage = {
  getItem: (name: string): string | null => {
    try {
      return globalThis.localStorage?.getItem(name) ?? null;
    } catch {
      // SILENT-CATCH: spec 112 FR-023 — storage unavailable must NOT surface; degrade to in-memory.
      return null;
    }
  },
  setItem: (name: string, value: string): void => {
    try {
      globalThis.localStorage?.setItem(name, value);
    } catch {
      // SILENT-CATCH: spec 112 FR-023 — storage write failure (quota / private mode) must degrade silently.
    }
  },
  removeItem: (name: string): void => {
    try {
      globalThis.localStorage?.removeItem(name);
    } catch {
      // SILENT-CATCH: spec 112 FR-023 — symmetric with setItem; remove failure must not surface.
    }
  },
};

interface MarketLifecycleSeenState {
  /** Sparse map of `${wallet}:${market}:${stage}` → true. Only seen entries stored. */
  seen: Record<string, true>;
  isSeen: (key: string) => boolean;
  markSeen: (key: string) => void;
}

export const useMarketLifecycleSeenStore: UseBoundStore<StoreApi<MarketLifecycleSeenState>> =
  createTrackedStore<MarketLifecycleSeenState>('useMarketLifecycleSeenStore')(
    persist(
      (set, get) => ({
        seen: {},
        isSeen: (key: string): boolean => get().seen[key] === true,
        markSeen: (key: string): void => {
          if (get().seen[key] === true) return;
          set(
            (state: MarketLifecycleSeenState): Pick<MarketLifecycleSeenState, 'seen'> => ({
              seen: { ...state.seen, [key]: true },
            }),
          );
        },
      }),
      {
        name: 'morfi-market-lifecycle-seen',
        partialize: (state: MarketLifecycleSeenState): Pick<MarketLifecycleSeenState, 'seen'> => ({
          seen: state.seen,
        }),
        storage: createJSONStorage(() => SAFE_STORAGE),
      },
    ),
  );
